import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {OptimizeByName, OptimizeByValue} from 'common/src/modules/optimization/optimization.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import supportsAttributionSignals from '../../utils/optimization/supportsAttributionSignals';

const setOptimizeByInputSchema = z.object({
  optimizeBy: z
    .nativeEnum(OptimizeByName)
    .describe('The metric to optimize by (e.g., Clicks, Conversions, Click Through Conversions)'),
});

export default function setOptimizeByTool() {
  async function execute(
    params: ToolExecuteParams<typeof setOptimizeByInputSchema, OptimizationCardScratchpadState>,
  ) {
    const {optimizeBy: optimizeByName} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<OptimizationCardScratchpadState>(
      agentScratchpad,
      NodeType.GENERAL_OPTIMIZATION,
    );

    const optimizeByValue =
      getMatchingEnumValue(OptimizeByName, OptimizeByValue, optimizeByName) ||
      OptimizeByValue.Clicks;

    validatedScratchpad.card.configuration = {
      ...validatedScratchpad.card.configuration,
      optimizeBy: optimizeByValue,
    };

    // Clear or set attribution signals based on whether the new value supports them
    if (supportsAttributionSignals(optimizeByValue)) {
      // Set default attribution signals selection (all signals selected)
      validatedScratchpad.card.configuration.areAllAttributionSignalsSelected = true;
      validatedScratchpad.card.configuration.attributionSignalIds = [];
    } else {
      // Clear attribution signals
      validatedScratchpad.card.configuration.areAllAttributionSignalsSelected = false;
      validatedScratchpad.card.configuration.attributionSignalIds = [];
    }

    return toolResult(`Optimize by set to ${optimizeByName}`);
  }

  return tool<typeof setOptimizeByInputSchema, OptimizationCardScratchpadState>({
    name: V2CardAgentToolName.SetOptimizeBy,
    displayName: 'Setting optimize by',
    description:
      'Set the metric to optimize by (e.g., Clicks, Conversions, Video completions). Attribution signals are automatically cleared when switching to metrics that do not support them.',
    inputSchema: setOptimizeByInputSchema,
    execute,
  });
}
